import { z } from 'zod'
import { t } from 'i18next'
import {
  FreitextSchema,
  KmSchema,
  SignalSchema,
  ZugmeldestelleSchema
} from '../../Generic/schema/generic.schema'

// Default constants
const DEFAULT_AUFTRAG_95_10 = {
  enabled: true
}

const DEFAULT_AUFTRAG_95_20 = {
  enabled: false
}

const DEFAULT_AUFTRAG_95_30 = {
  enabled: false
}

const DEFAULT_AUFTRAG_95_40 = {
  enabled: false
}

export const DEFAULT_AUFTRAG_95_50 = {
  enabled: false,
  in_am: '' as const,
  type: '' as const
}

export const DEFAULT_AUFTRAG_95_53 = {
  enabled: false
}

export const DEFAULT_AUFTRAG_95_54 = {
  enabled: false
}

export const DEFAULT_AUFTRAG_95_95 = {
  enabled: false,
  freitext: ''
}

const DEFAULT_BEFEHL_95_FORM = {
  auftrag_95_10: DEFAULT_AUFTRAG_95_10,
  auftrag_95_20: DEFAULT_AUFTRAG_95_20,
  auftrag_95_30: DEFAULT_AUFTRAG_95_30,
  auftrag_95_40: DEFAULT_AUFTRAG_95_40,
  auftrag_95_50: DEFAULT_AUFTRAG_95_50,
  auftrag_95_53: DEFAULT_AUFTRAG_95_53,
  auftrag_95_54: DEFAULT_AUFTRAG_95_54,
  auftrag_95_95: DEFAULT_AUFTRAG_95_95
}

// Schema definitions
const Auftrag95_10Schema = z
  .object({
    enabled: z.boolean().default(DEFAULT_AUFTRAG_95_10.enabled)
  })
  .default(() => structuredClone(DEFAULT_AUFTRAG_95_10))

const Auftrag95_20Schema = z
  .object({
    enabled: z.boolean().default(DEFAULT_AUFTRAG_95_20.enabled)
  })
  .default(() => structuredClone(DEFAULT_AUFTRAG_95_20))

const Auftrag95_30Schema = z
  .object({
    enabled: z.boolean().default(DEFAULT_AUFTRAG_95_30.enabled)
  })
  .default(() => structuredClone(DEFAULT_AUFTRAG_95_30))

const Auftrag95_40Schema = z
  .object({
    enabled: z.boolean().default(DEFAULT_AUFTRAG_95_40.enabled)
  })
  .default(() => structuredClone(DEFAULT_AUFTRAG_95_40))

const Auftrag95_50Schema = z
  .object({
    enabled: z.boolean().default(DEFAULT_AUFTRAG_95_50.enabled),
    in_am: z.enum(['', 'in', 'am']).default(DEFAULT_AUFTRAG_95_50.in_am),
    type: z
      .enum(['', 'km', 'signal_zugmeldestelle'])
      .default(DEFAULT_AUFTRAG_95_50.type),
    km: KmSchema.optional(),
    signal: SignalSchema.optional(),
    zugmeldestelle: ZugmeldestelleSchema.optional()
  })
  .default(() => structuredClone(DEFAULT_AUFTRAG_95_50))

const Auftrag95_53Schema = z
  .object({
    enabled: z.boolean().default(DEFAULT_AUFTRAG_95_53.enabled)
  })
  .default(() => structuredClone(DEFAULT_AUFTRAG_95_53))

const Auftrag95_54Schema = z
  .object({
    enabled: z.boolean().default(DEFAULT_AUFTRAG_95_54.enabled)
  })
  .default(() => structuredClone(DEFAULT_AUFTRAG_95_54))

const Auftrag95_95Schema = z
  .object({
    enabled: z.boolean().default(DEFAULT_AUFTRAG_95_95.enabled),
    freitext: FreitextSchema
  })
  .default(() => structuredClone(DEFAULT_AUFTRAG_95_95))

// Base schema
const Befehl95FormSchemaBase = z
  .object({
    auftrag_95_10: Auftrag95_10Schema,
    auftrag_95_20: Auftrag95_20Schema,
    auftrag_95_30: Auftrag95_30Schema,
    auftrag_95_40: Auftrag95_40Schema,
    auftrag_95_50: Auftrag95_50Schema,
    auftrag_95_53: Auftrag95_53Schema,
    auftrag_95_54: Auftrag95_54Schema,
    auftrag_95_95: Auftrag95_95Schema
  })
  .default(() => structuredClone(DEFAULT_BEFEHL_95_FORM))

export const Befehl95FormSchema = Befehl95FormSchemaBase

// Validation schema
export const createBefehl95FormValidationSchema = () => {
  return Befehl95FormSchema.refine(
    (data) => {
      return [
        data.auftrag_95_10.enabled,
        data.auftrag_95_20.enabled,
        data.auftrag_95_30.enabled,
        data.auftrag_95_40.enabled,
        data.auftrag_95_50.enabled,
        data.auftrag_95_95.enabled
      ].some(Boolean)
    },
    {
      message: t('ui-validation:befehl95.at_least_one')
    }
  ).refine(
    (data) => {
      // If 95.50 is enabled, at least one of 95.53 or 95.54 must be selected
      if (data.auftrag_95_50.enabled) {
        return data.auftrag_95_53.enabled || data.auftrag_95_54.enabled
      }
      return true
    },
    {
      message: t(
        'ui-validation:befehl95.pzb_requires_staendig_wirksam_or_unwirksam'
      )
    }
  )
}

export type Befehl95FormData = z.infer<typeof Befehl95FormSchemaBase>
